import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ContactPhotoService } from '../../core/services/contact-photo.service';
import { TranslatePipe } from '../../core/features/translations';

/**
 * Contact Photo Upload Component
 *
 * Provides UI for uploading/updating/deleting contact photos
 * Features:
 * - Drag & drop support
 * - File type validation (images only)
 * - File size validation (max 10MB)
 * - Preview before upload
 * - Upload/update/delete operations
 */
@Component({
  selector: 'app-contact-photo-upload',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  template: `
    <div class="flex flex-col gap-3 mt-4 max-w-[200px] font-sans">
      <!-- Upload Button or Drag Zone -->
      @if (!previewUrl()) {
      <div
        class="relative flex flex-col items-center justify-center min-h-[120px] p-8 border-1.5 border-dashed border-gray-300 rounded-md bg-gradient-to-b from-gray-50 to-gray-100 cursor-pointer overflow-hidden transition-all duration-300 hover:border-blue-600 hover:bg-gradient-to-b hover:from-blue-50 hover:to-blue-100 hover:-translate-y-0.5 hover:shadow-lg group"
        [class.border-blue-600.border-[3px].bg-gradient-to-b.from-blue-100.to-blue-200.scale-105]="isDragging()"
        (dragover)="onDragOver($event)"
        (dragleave)="onDragLeave($event)"
        (drop)="onDrop($event)"
      >
        <!-- Hover Gradient -->
        <div class="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>

        <input
          #fileInput
          type="file"
          accept="image/jpeg,image/jpg,.jpg,.jpeg"
          class="hidden"
          (change)="onFileSelected($event)"
        />

        <button
          class="relative z-10 w-full max-w-[180px] h-11 bg-blue-600 text-white border-0 rounded-md text-xs font-medium tracking-tight shadow-sm transition-all duration-300 flex items-center justify-center gap-1.5 hover:bg-blue-700 hover:shadow-md hover:-translate-y-px disabled:opacity-60 disabled:cursor-not-allowed"
          (click)="fileInput.click()"
          [disabled]="uploading()"
          type="button"
        >
          <span class="text-base">{{ hasExistingPhoto() ? '✏️' : '📷' }}</span>
          {{
            (hasExistingPhoto() ? 'portal.mydata.photo.change' : 'portal.mydata.photo.upload')
              | translate
          }}
        </button>

        <p class="relative z-10 mt-4 text-[10px] text-gray-500 font-normal leading-relaxed text-center px-1.5">{{ 'portal.mydata.photo.dragdrop' | translate }}</p>
      </div>
      }

      <!-- Preview & Actions -->
      @if (previewUrl()) {
      <div class="flex flex-col gap-3.5 items-center p-3.5 bg-gradient-to-b from-gray-50 to-gray-100 border border-gray-200 rounded-md max-w-[200px]">
        <img [src]="previewUrl()!" alt="Preview" 
             class="w-[180px] h-[180px] object-cover rounded-md shadow-sm transition-transform duration-300 hover:scale-[1.02]" />

        <div class="flex flex-col gap-2.5 w-full">
          <button
            class="w-full h-10 bg-blue-600 text-white border-0 rounded-md text-[11px] font-medium transition-all duration-300 flex items-center justify-center gap-1.5 hover:bg-blue-700 hover:-translate-y-px disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
            (click)="confirmUpload()"
            [disabled]="uploading()"
            type="button"
          >
            @if (uploading()) {
            <div class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            } @else {
            <span class="text-base">☁️</span>
            }
            {{
              (hasExistingPhoto() ? 'portal.mydata.photo.update' : 'portal.mydata.photo.uploadnow')
                | translate
            }}
          </button>

          <button
            class="w-full h-10 bg-white text-gray-600 border border-gray-200 rounded-md text-[11px] font-medium transition-all duration-300 flex items-center justify-center gap-1.5 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            (click)="cancelPreview()"
            [disabled]="uploading()"
            type="button"
          >
            <span class="text-base">❌</span>
            {{ 'portal.mydata.photo.cancel' | translate }}
          </button>
        </div>

        <!-- File Info -->
        @if (selectedFile()) {
        <div class="flex flex-col gap-1 w-full p-2.5 bg-white border border-gray-200 rounded-md text-center box-border">
          <span class="font-medium text-[10px] text-gray-800 break-words leading-tight">{{ selectedFile()!.name }}</span>
          <span class="text-[9px] text-gray-500">{{ formatFileSize(selectedFile()!.size) }}</span>
        </div>
        }
      </div>
      }

      <!-- Delete Button (only if photo exists and not in preview mode) -->
      @if (hasExistingPhoto() && !previewUrl() && showDeleteButton()) {
      <button
        class="w-full max-w-[180px] h-10 bg-white text-red-700 border border-red-700 rounded-md text-[11px] font-medium transition-all duration-300 flex items-center justify-center gap-1.5 hover:bg-red-50 hover:-translate-y-px disabled:opacity-60 disabled:cursor-not-allowed mx-auto"
        (click)="deletePhoto()"
        [disabled]="uploading()"
        type="button"
        [title]="'portal.mydata.photo.deleteTitle' | translate"
      >
        <span class="text-base">🗑️</span>
        {{ 'portal.mydata.photo.delete' | translate }}
      </button>
      }

      <!-- Messages -->
      @if (message()) {
      <div class="p-2 px-3 rounded text-[11px] text-center"
           [class.bg-green-50.text-green-800.border.border-green-500]="messageType() === 'success'"
           [class.bg-red-50.text-red-800.border.border-red-500]="messageType() === 'error'">
        {{ message() }}
      </div>
      }
    </div>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactPhotoUploadComponent {
  private contactPhotoService = inject(ContactPhotoService);

  // Inputs
  readonly contactId = input.required<number>();
  readonly hasExistingPhoto = input<boolean>(false);
  readonly showDeleteButton = input<boolean>(true);

  // Outputs
  readonly photoUploaded = output<void>();
  readonly photoDeleted = output<void>();
  readonly uploadError = output<string>();

  // State
  readonly selectedFile = signal<File | null>(null);
  readonly previewUrl = signal<string | null>(null);
  readonly uploading = signal(false);
  readonly isDragging = signal(false);
  readonly message = signal<string>('');
  readonly messageType = signal<'success' | 'error'>('success');

  /**
   * Handle file input change
   */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
    }
  }

  /**
   * Handle drag over
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(true);
  }

  /**
   * Handle drag leave
   */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  /**
   * Handle file drop
   */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.handleFile(event.dataTransfer.files[0]);
    }
  }

  /**
   * Handle file selection/drop
   */
  private handleFile(file: File): void {
    // Validate file type - nur JPG/JPEG
    const allowedTypes = ['image/jpeg', 'image/jpg'];
    const isValidType =
      allowedTypes.includes(file.type) ||
      file.name.toLowerCase().endsWith('.jpg') ||
      file.name.toLowerCase().endsWith('.jpeg');

    if (!isValidType) {
      this.showMessage('portal.mydata.photo.error.filetype', 'error');
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      this.showMessage('portal.mydata.photo.error.filesize', 'error');
      return;
    }

    this.selectedFile.set(file);

    // Create preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      this.previewUrl.set(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  }

  /**
   * Confirm and upload the selected file
   */
  confirmUpload(): void {
    const file = this.selectedFile();
    if (!file) return;

    this.uploading.set(true);

    this.contactPhotoService.uploadContactPhoto(this.contactId(), file, true).subscribe({
      next: (response) => {
        this.uploading.set(false);
        this.showMessage(
          this.hasExistingPhoto()
            ? 'portal.mydata.photo.success.updated'
            : 'portal.mydata.photo.success.uploaded',
          'success'
        );
        this.resetUpload();
        this.photoUploaded.emit();
      },
      error: (error) => {
        this.uploading.set(false);
        const errorMessage = error.error?.detail || 'portal.mydata.photo.error.upload';
        this.showMessage(errorMessage, 'error');
        this.uploadError.emit(errorMessage);
      },
    });
  }

  /**
   * Cancel preview
   */
  cancelPreview(): void {
    this.resetUpload();
  }

  /**
   * Delete photo
   */
  deletePhoto(): void {
    if (!confirm('portal.mydata.photo.deleteConfirm')) {
      return;
    }

    this.uploading.set(true);

    this.contactPhotoService.deleteContactPhoto(this.contactId(), 'jpg').subscribe({
      next: () => {
        this.uploading.set(false);
        this.showMessage('portal.mydata.photo.success.deleted', 'success');
        this.photoDeleted.emit();
      },
      error: (error) => {
        this.uploading.set(false);
        const errorMessage = error.error?.detail || 'portal.mydata.photo.error.delete';
        this.showMessage(errorMessage, 'error');
      },
    });
  }

  /**
   * Reset upload state
   */
  private resetUpload(): void {
    // Revoke preview URL to free memory
    if (this.previewUrl()) {
      URL.revokeObjectURL(this.previewUrl()!);
    }

    this.selectedFile.set(null);
    this.previewUrl.set(null);
  }

  /**
   * Show message
   */
  private showMessage(msg: string, type: 'success' | 'error'): void {
    this.message.set(msg);
    this.messageType.set(type);

    // Clear message after 3 seconds
    setTimeout(() => {
      this.message.set('');
    }, 3000);
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }
}
